// Хуки данных: загрузка с сервера, обновление города, живое обновление, pull-to-refresh.
import { get } from '@/api/client';
import type { CastleView, IncomingAttack, MarchView } from '@/api/types';
import { errorText } from '@/lib/errors';
import { useGame } from '@/store/game';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useApi<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!path);
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await get<T>(path));
      setError(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, loading, reload, setData };
}

const CASTLE_KEY = 'sk_castle';

/** Выбранный замок (захваченные замки тоже можно развивать); null — главный. */
export function selectedCastleId(): string | null {
  try {
    return localStorage.getItem(CASTLE_KEY);
  } catch {
    return null;
  }
}

export async function reloadCastle(): Promise<CastleView> {
  const id = selectedCastleId();
  try {
    const c = await get<CastleView>(id ? `/castle/me?castleId=${encodeURIComponent(id)}` : '/castle/me');
    useGame.getState().setCastle(c);
    return c;
  } catch (e) {
    // Замок потерян (захвачен врагом) — возвращаемся к главному
    if (id) {
      localStorage.removeItem(CASTLE_KEY);
      return reloadCastle();
    }
    throw e;
  }
}

/** Переключиться на другой свой замок. */
export async function selectCastle(id: string | null): Promise<void> {
  if (id) localStorage.setItem(CASTLE_KEY, id);
  else localStorage.removeItem(CASTLE_KEY);
  await reloadCastle();
}

export function useCastle() {
  const castle = useGame((s) => s.castle);
  useEffect(() => {
    if (!castle) void reloadCastle().catch(() => undefined);
  }, [castle]);
  return castle;
}

/** Pull-to-refresh: тянем вниз в верхней точке прокрутки. */
export function usePullToRefresh(onRefresh: () => Promise<unknown>) {
  const startY = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const handlers = {
    onTouchStart: (e: React.TouchEvent) => {
      if (window.scrollY <= 0 && (e.currentTarget as HTMLElement).scrollTop <= 0)
        startY.current = e.touches[0]?.clientY ?? null;
    },
    onTouchMove: (e: React.TouchEvent) => {
      if (startY.current === null) return;
      setPull(Math.max(0, Math.min(90, (e.touches[0]?.clientY ?? 0) - startY.current)));
    },
    onTouchEnd: async () => {
      if (pull > 70 && !refreshing) {
        setRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
        }
      }
      startY.current = null;
      setPull(0);
    },
  };
  return { pull, refreshing, handlers };
}

/** Подсказка при первом входе на экран (один раз). */
export function useFirstVisitHint(key: string): [boolean, () => void] {
  const storageKey = `sk_hint_${key}`;
  const [show, setShow] = useState(() => !localStorage.getItem(storageKey));
  return [
    show,
    () => {
      localStorage.setItem(storageKey, '1');
      setShow(false);
    },
  ];
}

export async function loadMarches(): Promise<void> {
  const userId = useGame.getState().user?.id;
  const r = await get<{
    own: MarchView[];
    incoming: IncomingAttack[];
  }>('/march/list');
  if (useGame.getState().user?.id === userId) useGame.getState().setMarches(r.own, r.incoming);
}

// ───── Живое обновление ─────
// Игра в браузере не должна требовать перезагрузки страницы: когда на сервере что-то завершилось
// (стройка, найм, поход, улучшение), все открытые экраны перечитывают свои данные.
const gameListeners = new Set<() => void>();
let gameTimer: number | null = null;

/** Сообщить, что состояние игры изменилось (события сокета, переподключение, таймеры). */
export function notifyGameChanged(): void {
  // Пачка событий за доли секунды (поход вернулся + ресурсы + квест) — одно обновление
  if (gameTimer != null) return;
  gameTimer = window.setTimeout(() => {
    gameTimer = null;
    for (const fn of gameListeners) fn();
  }, 250);
}

export function onGameChanged(fn: () => void): () => void {
  gameListeners.add(fn);
  return () => {
    gameListeners.delete(fn);
  };
}

/** Перечитывать данные экрана при каждом изменении состояния игры. */
export function useLiveReload(reload: () => Promise<unknown> | unknown): void {
  const ref = useRef(reload);
  ref.current = reload;
  useEffect(() => onGameChanged(() => void Promise.resolve(ref.current()).catch(() => undefined)), []);
}

let worldLoading: Promise<void> | null = null;

/** Размер текущего мира (нужен для морских маршрутов); грузится один раз и кладётся в стор. */
export function useWorldSize(): number | null {
  const seed = useGame((s) => s.worldSeed);
  const size = useGame((s) => s.worldSize);
  useEffect(() => {
    if (seed != null || worldLoading) return;
    worldLoading = get<{ seed: number; size: number }>('/world/current')
      .then((w) => useGame.getState().setWorld(w.seed, w.size))
      .catch(() => undefined)
      .finally(() => {
        worldLoading = null;
      });
  }, [seed]);
  return seed != null ? size : null;
}
